import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, PageHeader, Badge } from '@/components/ui'
import { adminApi } from '@/lib/api'

export const Route = createFileRoute('/store/$id/settings')({
  component: StoreSettingsPage,
})

interface Tenant {
  id: string
  slug: string
  name: string
  primaryHostname: string | null
  status: string
  defaultCurrency: string
  defaultLocale: string
  branding: Record<string, unknown>
  metadata: Record<string, string>
  createdAt: string
  updatedAt: string
}

function StoreSettingsPage() {
  const { id } = Route.useParams()

  const { data } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => adminApi.get<{ data: Tenant }>(`/api/admin/tenants/${id}`),
  })

  if (!data) return <div className="p-8 text-neutral-400">Cargando...</div>
  const t = data.data

  return (
    <>
      <PageHeader title="Ajustes" description="Configuración de la tienda" />
      <div className="p-8 space-y-6">
        <Card>
          <CardHeader><CardTitle>Información general</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Row label="Nombre" value={t.name} />
              <Row label="Slug" value={t.slug} mono />
              <Row label="Hostname" value={t.primaryHostname ?? '—'} mono />
              <Row label="Status" value={t.status} />
              <Row label="Currency default" value={t.defaultCurrency} />
              <Row label="Locale default" value={t.defaultLocale} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Branding</CardTitle>
              <Badge variant="muted">solo lectura</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {Object.keys(t.branding).length === 0 ? (
              <p className="text-sm text-neutral-500">Sin branding configurado.</p>
            ) : (
              <pre className="text-xs font-mono bg-neutral-950 p-3 rounded border border-neutral-800 overflow-x-auto">
                {JSON.stringify(t.branding, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Metadata</CardTitle>
              <Badge variant="muted">solo lectura</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {Object.keys(t.metadata).length === 0 ? (
              <p className="text-sm text-neutral-500">Sin metadata configurada.</p>
            ) : (
              <pre className="text-xs font-mono bg-neutral-950 p-3 rounded border border-neutral-800 overflow-x-auto">
                {JSON.stringify(t.metadata, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card className="bg-yellow-900/10 border-yellow-800">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-400 font-medium">Edición pendiente</p>
            <p className="text-xs text-neutral-300 mt-1">
              Para editar nombre, hostname, currency, branding y metadata, falta exponer endpoints PATCH
              y formularios de admin. Ahora mismo el endpoint <code>PATCH /api/admin/tenants/:id</code>
              existe pero no hay UI conectada. Próxima iteración.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-neutral-500">{label}</dt>
      <dd className={mono ? 'font-mono text-neutral-200' : 'text-neutral-200'}>{value}</dd>
    </>
  )
}
